/**
 * useGameRegistry Hook
 * Fetch and manage game definitions and weekend configurations
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import {
  GameDefinition,
  WeekendGameConfig,
  GameWithConfig,
} from "../types";
import { CompetitionSettings } from "@/types";

interface UseGameRegistryReturn {
  /** All registered games */
  games: GameDefinition[];
  /** Games with weekend configuration applied */
  gamesWithConfig: GameWithConfig[];
  /** Games available for weekend competition */
  weekendGames: GameWithConfig[];
  /** Games available for free play */
  freePlayGames: GameWithConfig[];
  /** Weekend game configurations */
  weekendConfigs: WeekendGameConfig[];
  /** Competition settings for active weekend */
  competitionSettings: CompetitionSettings | null;
  /** Whether competition is currently active */
  isCompetitionActive: boolean;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Refetch all data */
  refetch: () => Promise<void>;
  /** Get a specific game by slug */
  getGame: (slug: string) => GameWithConfig | undefined;
  /** Check if a game is available for weekend play right now */
  isGameAvailableForWeekend: (slug: string) => boolean;
}

/**
 * Hook to access the game registry and weekend configurations
 */
export const useGameRegistry = (): UseGameRegistryReturn => {
  const { activeWeekend } = useAuth();

  const [games, setGames] = useState<GameDefinition[]>([]);
  const [weekendConfigs, setWeekendConfigs] = useState<WeekendGameConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Fetch all game definitions
   */
  const fetchGames = useCallback(async () => {
    const { data, error: fetchError } = await supabase
      .from("game_definitions")
      .select("*")
      .eq("is_active", true)
      .order("sort_order", { ascending: true });

    if (fetchError) {
      throw new Error(fetchError.message);
    }

    return data as GameDefinition[];
  }, []);

  /**
   * Fetch weekend game configurations for active weekend
   */
  const fetchWeekendConfigs = useCallback(async () => {
    if (!activeWeekend?.id) {
      return [];
    }

    const { data, error: fetchError } = await supabase
      .from("weekend_game_config")
      .select("*")
      .eq("weekend_id", activeWeekend.id)
      .order("order_index", { ascending: true });

    if (fetchError) {
      throw new Error(fetchError.message);
    }

    return data as WeekendGameConfig[];
  }, [activeWeekend?.id]);

  /**
   * Fetch all data
   */
  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [gamesData, configsData] = await Promise.all([
        fetchGames(),
        fetchWeekendConfigs(),
      ]);

      setGames(gamesData);
      setWeekendConfigs(configsData);
    } catch (err) {
      console.error("Error fetching game registry:", err);
      setError(err instanceof Error ? err : new Error("Failed to fetch games"));
    } finally {
      setIsLoading(false);
    }
  }, [fetchGames, fetchWeekendConfigs]);

  // Initial fetch
  useEffect(() => {
    refetch();
  }, [refetch]);

  /**
   * Parse competition settings from active weekend
   */
  const competitionSettings = useMemo((): CompetitionSettings | null => {
    if (!activeWeekend?.competition_settings) {
      return null;
    }

    const settings = activeWeekend.competition_settings as CompetitionSettings;
    return {
      mode: settings.mode || "global",
      global_starts_at: settings.global_starts_at || null,
      global_ends_at: settings.global_ends_at || null,
      show_leaderboard: settings.show_leaderboard !== false,
      leaderboard_scope: settings.leaderboard_scope || "all",
    };
  }, [activeWeekend?.competition_settings]);

  /**
   * Check if competition is currently active
   */
  const isCompetitionActive = useMemo(() => {
    if (!activeWeekend?.is_active || !competitionSettings) {
      return false;
    }

    const now = new Date();

    if (competitionSettings.mode === "global") {
      if (competitionSettings.global_starts_at) {
        const startsAt = new Date(competitionSettings.global_starts_at);
        if (now < startsAt) return false;
      }
      if (competitionSettings.global_ends_at) {
        const endsAt = new Date(competitionSettings.global_ends_at);
        if (now > endsAt) return false;
      }
    }

    return true;
  }, [activeWeekend?.is_active, competitionSettings]);

  /**
   * Check if a specific game is available for weekend play now
   */
  const isGameAvailableForWeekend = useCallback(
    (slug: string): boolean => {
      if (!isCompetitionActive) return false;

      const config = weekendConfigs.find((c) => c.game_slug === slug);
      if (!config?.is_enabled) return false;

      const now = new Date();

      // Check custom dates if enabled
      if (config.use_custom_dates) {
        if (config.starts_at && now < new Date(config.starts_at)) return false;
        if (config.ends_at && now > new Date(config.ends_at)) return false;
      }

      return true;
    },
    [isCompetitionActive, weekendConfigs]
  );

  /**
   * Combine games with their weekend configs
   */
  const gamesWithConfig = useMemo((): GameWithConfig[] => {
    return games.map((game) => {
      const weekendConfig = weekendConfigs.find(
        (c) => c.game_slug === game.slug
      );
      const isAvailable = isGameAvailableForWeekend(game.slug);

      // Merge points config: weekend config overrides defaults
      const effectivePointsConfig = {
        ...game.default_points_config,
        ...(weekendConfig?.points_config || {}),
      };

      return {
        ...game,
        weekend_config: weekendConfig,
        is_available_now: isAvailable || game.supports_free_play,
        is_weekend_mode: isAvailable,
        effective_points_config: effectivePointsConfig,
      };
    });
  }, [games, weekendConfigs, isGameAvailableForWeekend]);

  /**
   * Games available for weekend competition
   */
  const weekendGames = useMemo((): GameWithConfig[] => {
    return gamesWithConfig.filter(
      (g) => g.supports_weekend && g.is_weekend_mode
    );
  }, [gamesWithConfig]);

  /**
   * Games available for free play
   */
  const freePlayGames = useMemo((): GameWithConfig[] => {
    return gamesWithConfig.filter((g) => g.supports_free_play);
  }, [gamesWithConfig]);

  /**
   * Get a specific game by slug
   */
  const getGame = useCallback(
    (slug: string): GameWithConfig | undefined => {
      return gamesWithConfig.find((g) => g.slug === slug);
    },
    [gamesWithConfig]
  );

  return {
    games,
    gamesWithConfig,
    weekendGames,
    freePlayGames,
    weekendConfigs,
    competitionSettings,
    isCompetitionActive,
    isLoading,
    error,
    refetch,
    getGame,
    isGameAvailableForWeekend,
  };
};

